import { ColumnDef } from '@tanstack/react-table'
import Status from 'features/common/status'
import { Box, chakra, Icon } from '@chakra-ui/react'
import { RiFlag2Fill } from 'react-icons/ri'
import { dateFormat, isDateWithinRange } from 'utils/date-time-utils'
import numeral from 'numeral'

export const TICKETS_TABLE_QUERY_KEYS = {
  woNumber: 'woNumber.contains',
  projectStatus: 'projectStatus.contains',
  flagCheck: 'flagCheck.equals',
  // lienRightExpireDate: 'lienRightExpireDate.equals',
  lienRightExpireDateStart: 'lienRightExpireDate.greaterThanOrEqual',
  lienRightExpireDateEnd: 'lienRightExpireDate.lessThanOrEqual',
  taskPriority: 'taskPriority.equals',
  streetAddress: 'streetAddress.contains',
  vendorSkillName: 'vendorSkillName.contains',
  daysInQueue: 'daysInQueue.equals',
  generalLabourName: 'generalLabourName.contains',
  zipCode: 'zipCode.contains',
  city: 'city.contains',
  clientName: 'clientName.contains',
  projectManager: 'projectManager.contains',
  // scheduledDateAndTime: 'workOrderDate.equals',
  scheduledDateAndTimeStart: 'workOrderDate.greaterThanOrEqual',
  scheduledDateAndTimeEnd: 'workOrderDate.lessThanOrEqual',
  workOrderIssueDescription: 'workOrderIssueDescription.contains',
  clientPhoneNumber: 'clientPhoneNumber.contains',
  expectedPaymentDateStart: 'expectedPaymentDate.greaterThanOrEqual',
  expectedPaymentDateEnd: 'expectedPaymentDate.lessThanOrEqual',
  displayPartialPayment: 'displayPartialPayment.contains',
  invoiceNumber: 'invoiceNumber.contains',
  // projectInvoiceDate: 'projectInvoiceDate.equals',
  projectInvoiceDateStart: 'projectInvoiceDate.greaterThanOrEqual',
  projectInvoiceDateEnd: 'projectInvoiceDate.lessThanOrEqual',
  displayAccountReceivable: 'displayAccountReceivable.contains',
  flag: 'flag.contains',
}
export const TICKETS_TABLE_COLUMNS: ColumnDef<any>[] = [
  {
    header: 'Ticket ID',
    accessorKey: 'woNumber',
    size: 120,
    cell: (row: any) => {
      const value = row.cell.getValue()
      const isFlagged = row.row?.original?.lienDueFlag
      return (
        <Box
          fontWeight={'500'}
          _hover={{
            color: 'barColor.50',
            textDecor: 'underline',
          }}
          color="brand.300"
        >
          {isFlagged && (
            <chakra.span marginRight={isFlagged ? '12px' : '26'}>
              <Icon
                data-testid={'lien_due_expiry_flag_icon'}
                title="Lien Due Expiry Flag"
                as={RiFlag2Fill}
                color="rgb(236, 201, 75,1)"
              />
            </chakra.span>
          )}

          {value}
        </Box>
      )
    },
  },
  {
    header: 'flag',
    accessorKey: 'flag',
  },
  {
    header: 'expectedPaymentDate',
    accessorKey: 'expectedPaymentDate',
    accessorFn: (cellInfo: any) => dateFormat(cellInfo.expectedPaymentDate),
    meta: { format: 'date' },
    cell: (row: any) => {
      const value = row?.row.original?.expectedPaymentDate
      return value ? dateFormat(value) : '- - -'
    },
    filterFn: (row: any, _columnId: string, filterValue: string) => {
      if (row.original.expectedPaymentDate) {
        return isDateWithinRange(row.original.expectedPaymentDate, filterValue)
      }
      return false
    },
  },
  {
    header: 'partialPayment',
    accessorKey: 'displayPartialPayment',
    accessorFn(cellInfo) {
      return numeral(Math.abs(cellInfo.displayPartialPayment)).format('$0,0.00')
    },
    meta: { format: 'currency' },
  },
  {
    header: 'Status',
    accessorKey: 'projectStatus',
    size: 130,
    cell: (row: any) => {
      const value = row.cell.getValue()
      return <Status value={value} id={value} />
    },
  },
  {
    header: 'Invoice Number',
    accessorKey: 'invoiceNumber',
  },
  {
    header: 'Invoice Date',
    accessorKey: 'projectInvoiceDate',
    // size: 130,
    accessorFn: (cellInfo: any) => dateFormat(cellInfo.projectInvoiceDate),
    meta: { format: 'date' },
    cell: (row: any) => {
      const value = row?.row.original?.projectInvoiceDate
      return value ? dateFormat(value) : '- - -'
    },
    filterFn: (row: any, _columnId: string, filterValue: string) => {
      if (row.original.projectInvoiceDate) {
        return isDateWithinRange(row.original.projectInvoiceDate, filterValue)
      }
      return false
    },
  },
  {
    header: 'AR',
    accessorKey: 'displayAccountReceivable',
    accessorFn: cellInfo => {
      return numeral(cellInfo.displayAccountReceivable).format('$0,0.00')
    },
    meta: { format: 'currency' },
  },
  {
    header: 'Lien Expiry',
    accessorKey: 'lienRightExpireDate',
    // size: 130,
    accessorFn: (cellInfo: any) => dateFormat(cellInfo.lienRightExpireDate),
    meta: { format: 'date' },
    cell: (row: any) => {
      const value = row?.row.original?.lienRightExpireDate
      return value ? dateFormat(value) : '- - -'
    },
    filterFn: (row: any, _columnId: string, filterValue: string) => {
      if (row.original.scheduledDateAndTime) {
        return isDateWithinRange(row.original.lienRightExpireDate, filterValue)
      }
      return false
    },
  },
  {
    header: 'Priority',
    accessorKey: 'taskPriority' as any,
    size: 150,
    cell: (row: any) => {
      const value = row.cell.getValue()
      return <Status value={value} id={value} />
    },
  },

  {
    header: 'Assigned To',
    accessorKey: 'projectManager',
    accessorFn: cellInfo => {
      return cellInfo.projectManager ? cellInfo.projectManager?.toString() : '- - -'
    },
  },

  {
    header: 'Trade',
    accessorKey: 'vendorSkillName',
    accessorFn: cellInfo => {
      return cellInfo.vendorSkillName ? cellInfo.vendorSkillName?.toString() : '- - -'
    },
  },

  {
    header: 'Description of Issue',
    accessorKey: 'workOrderIssueDescription',
    accessorFn: cellInfo => {
      return cellInfo?.workOrderIssueDescription ? cellInfo.workOrderIssueDescription?.toString() : '- - -'
    },
  },

  {
    header: 'Scheduled Date and Time',
    accessorKey: 'scheduledDateAndTime',
    accessorFn: (cellInfo: any) => dateFormat(cellInfo.scheduledDateAndTime),
    meta: { format: 'date' },
    cell: (row: any) => {
      const value = row?.row.original?.scheduledDateAndTime
      return value ? dateFormat(value) : '- - -'
    },
    filterFn: (row: any, _columnId: string, filterValue: string) => {
      if (row.original.scheduledDateAndTime) {
        return isDateWithinRange(row.original.scheduledDateAndTime, filterValue)
      }
      return false
    },
  },

  {
    header: 'DIQ',
    accessorKey: 'daysInQueue',
    accessorFn: cellInfo => {
      return cellInfo.daysInQueue ? cellInfo.daysInQueue?.toString() : '- - -'
    },
  },

  {
    header: 'Address',
    accessorKey: 'streetAddress',
  },

  {
    header: 'Vendor',
    accessorKey: 'generalLabourName',
  },

  {
    header: 'City',
    accessorKey: 'city',
  },

  {
    header: 'Zip Code',
    accessorKey: 'zipCode',
  },
  {
    header: 'Client Name',
    accessorKey: 'clientName' as any,
    accessorFn: cellInfo => {
      return cellInfo.clientName ? cellInfo.clientName?.toString() : '- - -'
    },
  },
  {
    header: 'Client Number',
    accessorKey: 'clientPhoneNumber' as any,
    accessorFn: cellInfo => {
      return cellInfo.clientPhoneNumber ? cellInfo.clientPhoneNumber?.toString() : '- - -'
    },
  },
  /*{
    header: "Slot 1",
    accessorKey: "clientSchedule1",
    accessorFn:  (row, i) => {
      if ( ! row.clientSchedule  || ! row.clientSchedule[0].preferredStartTime ) return "- - -";
      return dateFormat(row.clientSchedule[0].preferredStartDate) + " " +  row.clientSchedule[0].preferredStartTime?.title
    }
  },
  {
    header: "Slot 2",
    accessorKey: "clientSchedule2",
    accessorFn: (row, i) => {
      if ( ! row.clientSchedule  || ! row.clientSchedule[1].preferredStartTime ) return "- - -";
      return dateFormat(row.clientSchedule[1].preferredStartDate) + " " +  row.clientSchedule[1].preferredStartTime?.title
    }
  },
  {
    header: "Slot 3",
    accessorKey: "clientSchedule3",
    accessorFn: (row, i) => {
      if ( ! row.clientSchedule  || ! row.clientSchedule[2].preferredStartTime ) return "- - -";
      return dateFormat(row.clientSchedule[2].preferredStartDate) + " " +  row.clientSchedule[2].preferredStartTime?.title
    }
  }*/

  /*{
    header: 'Final SOW',
    accessorKey: 'approvedSow',
    size: 110,
    accessorFn(cellInfo: any) {
      return numeral(cellInfo.approvedSow).format('$0,0.00')
    },
    meta: { format: 'currency' },
  },
  {
    header: 'Vendor Cost',
    accessorKey: 'vendorCost',
    size: 120,
    accessorFn(cellInfo: any) {
      return numeral(cellInfo.vendorCost).format('$0,0.00')
    },
    meta: { format: 'currency' },
  },
  {
    header: 'Change Order',
    accessorKey: 'changeOrder',
    size: 120,
    accessorFn(cellInfo: any) {
      return numeral(cellInfo.changeOrder).format('$0,0.00')
    },
    meta: { format: 'currency' },
  },
  {
    header: 'Material',
    accessorKey: 'material',
    size: 100,
    accessorFn(cellInfo: any) {
      return numeral(cellInfo.material).format('$0,0.00')
    },
    meta: { format: 'currency' },
  },

  {
    header: 'Profit',
    accessorKey: 'profit',
    size: 100,
    accessorFn(cellInfo: any) {
      return numeral(cellInfo.profit).format('$0,0.00')
    },
    meta: { format: 'currency' },
  },
  {
    header: 'Margin',
    accessorKey: 'profitMargin',
    size: 100,
    accessorFn(cellInfo: any) {
      return numeral(cellInfo.profitMargin / 100).format('0.00%')
    },
  }, */
]
